import { useId } from "react";
import type { HttpAuthType } from "@/types/api";
import { HttpAuthFields } from "./HttpAuthFields";
import { StepperInput } from "./StepperInput";
import {
  FieldRowEven,
  FormGroup,
  FormSection,
  Input,
  Label,
  SectionTitle,
} from "./styles";

interface HttpFieldsProps {
  httpHost: string;
  setHttpHost: (value: string) => void;
  maxRetries: string;
  setMaxRetries: (value: string) => void;
  connectionTimeoutSec: string;
  setConnectionTimeoutSec: (value: string) => void;
  timeoutSec: string;
  setTimeoutSec: (value: string) => void;
  authType: HttpAuthType | "none";
  setAuthType: (value: HttpAuthType | "none") => void;
  bearerToken: string;
  setBearerToken: (value: string) => void;
  basicUsername: string;
  setBasicUsername: (value: string) => void;
  basicPassword: string;
  setBasicPassword: (value: string) => void;
  apiKeyKey: string;
  setApiKeyKey: (value: string) => void;
  apiKeyParamName: string;
  setApiKeyParamName: (value: string) => void;
  headerName: string;
  setHeaderName: (value: string) => void;
  headerValue: string;
  setHeaderValue: (value: string) => void;
}

export function HttpFields({
  httpHost,
  setHttpHost,
  maxRetries,
  setMaxRetries,
  connectionTimeoutSec,
  setConnectionTimeoutSec,
  timeoutSec,
  setTimeoutSec,
  authType,
  setAuthType,
  bearerToken,
  setBearerToken,
  basicUsername,
  setBasicUsername,
  basicPassword,
  setBasicPassword,
  apiKeyKey,
  setApiKeyKey,
  apiKeyParamName,
  setApiKeyParamName,
  headerName,
  setHeaderName,
  headerValue,
  setHeaderValue,
}: HttpFieldsProps) {
  const httpHostId = useId();
  const maxRetriesId = useId();
  const connectionTimeoutSecId = useId();
  const timeoutSecId = useId();

  return (
    <>
      <FormSection>
        <SectionTitle>HTTP Client configuration</SectionTitle>

        <FormGroup>
          <Label htmlFor={httpHostId}>Host URL*</Label>
          <Input
            id={httpHostId}
            type="text"
            value={httpHost}
            onChange={(e) => setHttpHost(e.target.value)}
            placeholder="https://api.example.com"
            required
          />
        </FormGroup>

        {/* Max retries + timeouts share one row per Figma 14055-95579; the
            timeouts carry their unit inside the input instead of the label. */}
        <FieldRowEven>
          <FormGroup>
            <Label htmlFor={maxRetriesId}>Max retries</Label>
            <StepperInput
              id={maxRetriesId}
              label="max retries"
              value={maxRetries}
              onChange={setMaxRetries}
              placeholder="5"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor={connectionTimeoutSecId}>Connection timeout</Label>
            <StepperInput
              id={connectionTimeoutSecId}
              label="connection timeout"
              value={connectionTimeoutSec}
              onChange={setConnectionTimeoutSec}
              placeholder="30"
              unit="s"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor={timeoutSecId}>Request timeout</Label>
            <StepperInput
              id={timeoutSecId}
              label="request timeout"
              value={timeoutSec}
              onChange={setTimeoutSec}
              placeholder="30"
              unit="s"
            />
          </FormGroup>
        </FieldRowEven>
      </FormSection>

      <HttpAuthFields
        authType={authType}
        setAuthType={setAuthType}
        bearerToken={bearerToken}
        setBearerToken={setBearerToken}
        basicUsername={basicUsername}
        setBasicUsername={setBasicUsername}
        basicPassword={basicPassword}
        setBasicPassword={setBasicPassword}
        apiKeyKey={apiKeyKey}
        setApiKeyKey={setApiKeyKey}
        apiKeyParamName={apiKeyParamName}
        setApiKeyParamName={setApiKeyParamName}
        headerName={headerName}
        setHeaderName={setHeaderName}
        headerValue={headerValue}
        setHeaderValue={setHeaderValue}
      />
    </>
  );
}
